import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { BatteryChargingIcon, SaveIcon, XIcon, EditIcon } from 'lucide-react';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { collection, doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
interface ChargerPrices {
  'Zaptec 1': number;
  'Zaptec 2': number;
  Andet: number;
}
interface ChargerSettings {
  prices: ChargerPrices;
  averageSalePrice?: number;
}
interface ChargerMaterial {
  name: string;
  price: number;
  quantity: number;
}
const defaultChargerMaterials: ChargerMaterial[] = [{
  name: 'Kombiafbr.',
  price: 465.11,
  quantity: 1
}, {
  name: 'Ladeboks',
  price: 1600.0,
  quantity: 1
}, {
  name: 'Kabel 5g4',
  price: 432.16,
  quantity: 20
}, {
  name: 'Tavle UG 150',
  price: 71.43,
  quantity: 1
}, {
  name: 'Diverse små ting',
  price: 240.0,
  quantity: 1
}, {
  name: 'ELCON Timeløn',
  price: 545.0,
  quantity: 1
}];
export function ChargerSettings() {
  const [prices, setPrices] = useState<ChargerPrices>({
    'Zaptec 1': 1046.3,
    'Zaptec 2': 4321.54,
    Andet: 0
  });
  const [averageSalePrice, setAverageSalePrice] = useState<number>(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingMaterials, setIsEditingMaterials] = useState(false);
  const [tempPrices, setTempPrices] = useState<ChargerPrices>(prices);
  const [tempAverageSalePrice, setTempAverageSalePrice] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // Materials state
  const [chargerMaterials, setChargerMaterials] = useState<ChargerMaterial[]>(() => {
    const saved = localStorage.getItem('chargerMaterials');
    return saved ? JSON.parse(saved) : defaultChargerMaterials;
  });
  const [installationPrice, setInstallationPrice] = useState(() => {
    const saved = localStorage.getItem('installationPrice');
    return saved ? parseFloat(saved) : 4400.0;
  });
  // Auto-save materials to localStorage
  useEffect(() => {
    localStorage.setItem('chargerMaterials', JSON.stringify(chargerMaterials));
  }, [chargerMaterials]);
  useEffect(() => {
    localStorage.setItem('installationPrice', installationPrice.toString());
  }, [installationPrice]);
  // Load prices from Firebase on mount
  useEffect(() => {
    const loadPrices = async () => {
      try {
        const docRef = doc(db, 'settings', 'chargerPrices');
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          // Support both old format (just prices) and new format (with averageSalePrice)
          if (data.prices) {
            // New format
            const loadedPrices = data.prices as ChargerPrices;
            const loadedAverage = data.averageSalePrice || 0;
            setPrices(loadedPrices);
            setAverageSalePrice(loadedAverage);
            setTempPrices(loadedPrices);
            setTempAverageSalePrice(loadedAverage);
          } else {
            // Old format - migrate to new format
            const loadedPrices = data as ChargerPrices;
            setPrices(loadedPrices);
            setAverageSalePrice(0);
            setTempPrices(loadedPrices);
            setTempAverageSalePrice(0);
          }
        } else {
          // Initialize with default prices if not exists
          const defaultPrices: ChargerPrices = {
            'Zaptec 1': 1046.3,
            'Zaptec 2': 4321.54,
            Andet: 0
          };
          await setDoc(docRef, {
            prices: defaultPrices,
            averageSalePrice: 0
          });
          setPrices(defaultPrices);
          setAverageSalePrice(0);
          setTempPrices(defaultPrices);
          setTempAverageSalePrice(0);
        }
      } catch (error) {
        console.error('Error loading charger prices:', error);
        alert('Fejl ved indlæsning af priser. Tjek konsollen for detaljer.');
      } finally {
        setIsLoading(false);
      }
    };
    loadPrices();
  }, []);
  const handleEdit = () => {
    setTempPrices(prices);
    setTempAverageSalePrice(averageSalePrice);
    setIsEditing(true);
  };
  const handleCancel = () => {
    setTempPrices(prices);
    setTempAverageSalePrice(averageSalePrice);
    setIsEditing(false);
  };
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const docRef = doc(db, 'settings', 'chargerPrices');
      await setDoc(docRef, {
        prices: tempPrices,
        averageSalePrice: tempAverageSalePrice
      });
      setPrices(tempPrices);
      setAverageSalePrice(tempAverageSalePrice);
      setIsEditing(false);
      // Show success message
      alert('Indstillinger gemt! Alle brugere vil nu se de opdaterede værdier.');
    } catch (error) {
      console.error('Error saving charger settings:', error);
      alert('Fejl ved gemning af indstillinger. Tjek konsollen for detaljer.');
    } finally {
      setIsSaving(false);
    }
  };
  const updatePrice = (type: keyof ChargerPrices, value: string) => {
    setTempPrices({
      ...tempPrices,
      [type]: parseFloat(value) || 0
    });
  };
  // Calculate charger profit
  const materialsCost = useMemo(() => {
    return chargerMaterials.reduce((sum, material) => sum + material.price * material.quantity, 0);
  }, [chargerMaterials]);
  const chargerProfit = installationPrice - materialsCost;
  const handleSaveMaterials = () => {
    setIsEditingMaterials(false);
  };
  const handleCancelMaterialsEdit = () => {
    const saved = localStorage.getItem('chargerMaterials');
    setChargerMaterials(saved ? JSON.parse(saved) : defaultChargerMaterials);
    const savedPrice = localStorage.getItem('installationPrice');
    setInstallationPrice(savedPrice ? parseFloat(savedPrice) : 4400.0);
    setIsEditingMaterials(false);
  };
  const updateMaterial = (index: number, field: keyof ChargerMaterial, value: string | number) => {
    const updated = [...chargerMaterials];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    setChargerMaterials(updated);
  };
  if (isLoading) {
    return <div className="space-y-6" data-id="element-1113">
        <header className="mb-8" data-id="element-1114">
          <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1115">
            Ladeboks information
          </h1>
          <p className="text-stone-600" data-id="element-1116">Indlæser indstillinger...</p>
        </header>
      </div>;
  }
  return <div className="space-y-6" data-id="element-1117">
      <header className="mb-8" data-id="element-1118">
        <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1119">
          Ladeboks information
        </h1>
        <p className="text-stone-600" data-id="element-1120">
          Administrer materialepriser og gennemsnitlige salgspriser for
          ladebokse
        </p>
      </header>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm" data-id="element-1121">
        <div className="flex items-center justify-between mb-6" data-id="element-1122">
          <div className="flex items-center gap-3" data-id="element-1123">
            <div className="p-3 bg-teal-100 rounded-xl" data-id="element-1124">
              <BatteryChargingIcon className="w-6 h-6 text-teal-700" data-id="element-1125" />
            </div>
            <div data-id="element-1126">
              <h2 className="text-lg font-semibold text-stone-900" data-id="element-1127">
                Ladeboks materialepriser
              </h2>
              <p className="text-sm text-stone-500" data-id="element-1128">
                Priser bruges automatisk ved oprettelse af opgaver
              </p>
            </div>
          </div>

          {!isEditing ? <Button onClick={handleEdit} variant="primary" data-id="element-1129">
              Rediger indstillinger
            </Button> : <div className="flex gap-2" data-id="element-1130">
              <Button onClick={handleCancel} variant="secondary" className="flex items-center gap-2" disabled={isSaving} data-id="element-1131">
                <XIcon className="w-4 h-4" data-id="element-1132" />
                Annuller
              </Button>
              <Button onClick={handleSave} variant="primary" className="flex items-center gap-2" disabled={isSaving} data-id="element-1133">
                <SaveIcon className="w-4 h-4" data-id="element-1134" />
                {isSaving ? 'Gemmer...' : 'Gem ændringer'}
              </Button>
            </div>}
        </div>

        <div className="space-y-4" data-id="element-1135">
          {/* Zaptec 1 */}
          <div className="p-4 bg-stone-50 rounded-xl border border-stone-200" data-id="element-1136">
            <div className="flex items-center justify-between" data-id="element-1137">
              <div className="flex-1" data-id="element-1138">
                <h3 className="font-semibold text-stone-900 mb-1" data-id="element-1139">Zaptec 1</h3>
                <p className="text-sm text-stone-500" data-id="element-1140">Standard ladeboks type</p>
              </div>
              <div className="w-48" data-id="element-1141">
                {isEditing ? <Input label="Pris (kr)" type="number" step="0.01" value={tempPrices['Zaptec 1']} onChange={e => updatePrice('Zaptec 1', e.target.value)} data-id="element-1142" /> : <div className="text-right" data-id="element-1143">
                    <p className="text-sm text-stone-500" data-id="element-1144">Materialer pris</p>
                    <p className="text-2xl font-bold text-stone-900" data-id="element-1145">
                      {prices['Zaptec 1'].toLocaleString('da-DK', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}{' '}
                      kr
                    </p>
                  </div>}
              </div>
            </div>
          </div>

          {/* Zaptec 2 */}
          <div className="p-4 bg-stone-50 rounded-xl border border-stone-200" data-id="element-1146">
            <div className="flex items-center justify-between" data-id="element-1147">
              <div className="flex-1" data-id="element-1148">
                <h3 className="font-semibold text-stone-900 mb-1" data-id="element-1149">Zaptec 2</h3>
                <p className="text-sm text-stone-500" data-id="element-1150">Premium ladeboks type</p>
              </div>
              <div className="w-48" data-id="element-1151">
                {isEditing ? <Input label="Pris (kr)" type="number" step="0.01" value={tempPrices['Zaptec 2']} onChange={e => updatePrice('Zaptec 2', e.target.value)} data-id="element-1152" /> : <div className="text-right" data-id="element-1153">
                    <p className="text-sm text-stone-500" data-id="element-1154">Materialer pris</p>
                    <p className="text-2xl font-bold text-stone-900" data-id="element-1155">
                      {prices['Zaptec 2'].toLocaleString('da-DK', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}{' '}
                      kr
                    </p>
                  </div>}
              </div>
            </div>
          </div>

          {/* Andet */}
          <div className="p-4 bg-stone-50 rounded-xl border border-stone-200" data-id="element-1156">
            <div className="flex items-center justify-between" data-id="element-1157">
              <div className="flex-1" data-id="element-1158">
                <h3 className="font-semibold text-stone-900 mb-1" data-id="element-1159">Andet</h3>
                <p className="text-sm text-stone-500" data-id="element-1160">
                  Manuel indtastning af pris
                </p>
              </div>
              <div className="w-48" data-id="element-1161">
                <div className="text-right" data-id="element-1162">
                  <p className="text-sm text-stone-500" data-id="element-1163">Materialer pris</p>
                  <p className="text-lg font-medium text-stone-600" data-id="element-1164">
                    Manuel indtastning
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* NEW: Average Sale Price */}
          <div className="p-4 bg-amber-50 rounded-xl border border-amber-200" data-id="element-1165">
            <div className="flex items-center justify-between" data-id="element-1166">
              <div className="flex-1" data-id="element-1167">
                <h3 className="font-semibold text-stone-900 mb-1" data-id="element-1168">
                  Gennemsnit pr ladeboks salg
                </h3>
                <p className="text-sm text-stone-500" data-id="element-1169">
                  Gennemsnitlig salgspris for en ladeboks installation
                </p>
              </div>
              <div className="w-48" data-id="element-1170">
                {isEditing ? <Input label="Gennemsnitspris (kr)" type="number" step="0.01" value={tempAverageSalePrice} onChange={e => setTempAverageSalePrice(parseFloat(e.target.value) || 0)} data-id="element-1171" /> : <div className="text-right" data-id="element-1172">
                    <p className="text-sm text-stone-500" data-id="element-1173">Gennemsnitspris</p>
                    <p className="text-2xl font-bold text-amber-900" data-id="element-1174">
                      {averageSalePrice.toLocaleString('da-DK', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}{' '}
                      kr
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </div>

        {/* Info box */}
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg" data-id="element-1175">
          <p className="text-sm text-blue-900" data-id="element-1176">
            <strong data-id="element-1177">Bemærk:</strong> Når du vælger en ladeboks type ved
            oprettelse af en opgave, vil materialeprisen automatisk blive
            udfyldt med den pris du har angivet her. For "Andet" skal prisen
            indtastes manuelt.
          </p>
        </div>

        {/* Firebase sync info */}
        <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-lg" data-id="element-1178">
          <p className="text-sm text-teal-900" data-id="element-1179">
            <strong data-id="element-1180">🔥 Firebase Sync:</strong> Alle ændringer gemmes i skyen og
            deles automatisk med alle brugere på tværs af alle computere og
            enheder.
          </p>
        </div>
      </motion.div>

      {/* Charger Profit Calculator */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2
    }} className="bg-white rounded-2xl p-8 border border-stone-200 shadow-sm" data-id="element-1181">
        <div className="flex items-center justify-between mb-8" data-id="element-1182">
          <h2 className="text-lg font-semibold text-stone-900" data-id="element-1183">
            Gennemsnit pr ladeboks salg
          </h2>
          {!isEditingMaterials ? <button onClick={() => setIsEditingMaterials(true)} className="flex items-center gap-2 px-4 py-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" data-id="element-1184">
              <EditIcon className="w-4 h-4" data-id="element-1185" />
              Rediger
            </button> : <div className="flex gap-2" data-id="element-1186">
              <button onClick={handleCancelMaterialsEdit} className="flex items-center gap-2 px-4 py-2 text-stone-600 hover:bg-stone-100 rounded-lg transition-colors" data-id="element-1187">
                <XIcon className="w-4 h-4" data-id="element-1188" />
                Annuller
              </button>
              <button onClick={handleSaveMaterials} className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white hover:bg-teal-700 rounded-lg transition-colors" data-id="element-1189">
                <SaveIcon className="w-4 h-4" data-id="element-1190" />
                Gem
              </button>
            </div>}
        </div>

        <div className="overflow-x-auto" data-id="element-1191">
          <table className="w-full" data-id="element-1192">
            <thead data-id="element-1193">
              <tr className="border-b border-stone-200" data-id="element-1194">
                <th className="text-left py-3 px-4 text-sm font-semibold text-stone-700" data-id="element-1195">
                  Materialer
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-stone-700" data-id="element-1196">
                  Pris
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-stone-700" data-id="element-1197">
                  Stk
                </th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-stone-700" data-id="element-1198">
                  Total
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1199">
              {chargerMaterials.map((material, index) => <tr key={index} className="border-b border-stone-100" data-id="element-1200">
                  <td className="py-3 px-4" data-id="element-1201">
                    {isEditingMaterials ? <input type="text" value={material.name} onChange={e => updateMaterial(index, 'name', e.target.value)} className="w-full px-2 py-1 border border-stone-200 rounded" data-id="element-1202" /> : <span className="text-stone-900" data-id="element-1203">{material.name}</span>}
                  </td>
                  <td className="py-3 px-4 text-right" data-id="element-1204">
                    {isEditingMaterials ? <input type="number" step="0.01" value={material.price} onChange={e => updateMaterial(index, 'price', parseFloat(e.target.value) || 0)} className="w-24 px-2 py-1 border border-stone-200 rounded text-right" data-id="element-1205" /> : <span className="text-stone-900" data-id="element-1206">
                        kr {material.price.toFixed(2)}
                      </span>}
                  </td>
                  <td className="py-3 px-4 text-right" data-id="element-1207">
                    {isEditingMaterials ? <input type="number" value={material.quantity} onChange={e => updateMaterial(index, 'quantity', parseInt(e.target.value) || 0)} className="w-16 px-2 py-1 border border-stone-200 rounded text-right" data-id="element-1208" /> : <span className="text-stone-900" data-id="element-1209">
                        {material.quantity}
                      </span>}
                  </td>
                  <td className="py-3 px-4 text-right" data-id="element-1210">
                    <span className="text-stone-900 font-medium" data-id="element-1211">
                      kr {(material.price * material.quantity).toFixed(2)}
                    </span>
                  </td>
                </tr>)}
            </tbody>
            <tfoot data-id="element-1212">
              <tr className="border-t-2 border-stone-300 font-semibold" data-id="element-1213">
                <td colSpan={3} className="py-4 px-4 text-stone-900" data-id="element-1214">
                  Pris (Installation + boks)
                </td>
                <td className="py-4 px-4 text-right" data-id="element-1215">
                  {isEditingMaterials ? <input type="number" step="0.01" value={installationPrice} onChange={e => setInstallationPrice(parseFloat(e.target.value) || 0)} className="w-32 px-2 py-1 border border-stone-200 rounded text-right font-semibold" data-id="element-1216" /> : <span className="text-stone-900" data-id="element-1217">
                      kr {installationPrice.toFixed(2)}
                    </span>}
                </td>
              </tr>
              <tr className="font-semibold" data-id="element-1218">
                <td colSpan={3} className="py-3 px-4 text-stone-900" data-id="element-1219">
                  Pris (Materialer)
                </td>
                <td className="py-3 px-4 text-right text-stone-900" data-id="element-1220">
                  -kr {materialsCost.toFixed(2)}
                </td>
              </tr>
              <tr className="border-t-2 border-stone-300 font-bold text-lg" data-id="element-1221">
                <td colSpan={3} className="py-4 px-4 text-emerald-700" data-id="element-1222">
                  Profit pr stk.
                </td>
                <td className="py-4 px-4 text-right text-emerald-700" data-id="element-1223">
                  kr {chargerProfit.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </motion.div>
    </div>;
}